import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Shield,
  TrendingDown,
  Store,
  CheckCircle2,
  MapPin,
  MessageCircle,
  Star,
  Menu,
  X,
  Wrench,
  Zap,
  Building2,
  ArrowRight,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────

const WHATSAPP_URL = "https://wa.me/5511959205581";

const LOGO_URL = "/membrus-logo.svg";

const MembrusLogo = ({ width = 120, height = 32 }) => (
  <div
    style={{
      width,
      height,
      backgroundColor: "#1e3a8a",
      WebkitMaskImage: `url(${LOGO_URL})`,
      maskImage: `url(${LOGO_URL})`,
      WebkitMaskRepeat: "no-repeat",
      maskRepeat: "no-repeat",
      WebkitMaskSize: "contain",
      maskSize: "contain",
      WebkitMaskPosition: "center",
      maskPosition: "center",
    }}
    role="img"
    aria-label="Membrus"
  />
);

const PARTNER_LOGOS = [
  { name: "QuintoAndar", url: "/logos/quintoandar.webp" },
  { name: "Riachuelo",   url: "/logos/riachuelo.webp" },
  { name: "Tok&Stok",    url: "/logos/tokstok.webp" },
  { name: "Linx",        url: "/logos/linx.webp" },
  { name: "Betalabs",    url: "/logos/betalabs.webp" },
  { name: "1M2",         url: "/logos/1m2.webp" },
  { name: "Mypharma2go", url: "/logos/mypharma2go.webp" },
];

// ─────────────────────────────────────────────────────────────────────────────
// ANIMATION VARIANTS
// ─────────────────────────────────────────────────────────────────────────────

const ease = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 48 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease } },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13, delayChildren: 0.05 } },
};

const scaleUp = {
  hidden: { opacity: 0, scale: 0.88 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.55, ease } },
};

// ─────────────────────────────────────────────────────────────────────────────
// UTILITY
// ─────────────────────────────────────────────────────────────────────────────

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// ─────────────────────────────────────────────────────────────────────────────
// HEADER
// ─────────────────────────────────────────────────────────────────────────────

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const links = [
    { label: "O Problema", id: "problema" },
    { label: "Exemplos",   id: "exemplos" },
    { label: "Preço",      id: "preco" },
    { label: "Quem Somos", id: "quem-somos" },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <button
            data-testid="logo-button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-black text-2xl text-blue-900 tracking-tight hover:opacity-80 transition-opacity"
          >
            <MembrusLogo width={120} height={32} />
          </button>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="text-gray-600 hover:text-gray-900 text-sm font-medium transition-colors duration-200"
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <a
            data-testid="header-whatsapp-btn"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5"
          >
            <MessageCircle className="w-4 h-4" />
            Falar no WhatsApp
          </a>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-btn"
            className="md:hidden p-2 text-gray-600 hover:text-gray-900"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          className="md:hidden bg-white border-t border-gray-100 px-4 py-4 space-y-2"
        >
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`mobile-nav-${l.id}`}
              onClick={() => { scrollTo(l.id); setMobileOpen(false); }}
              className="block w-full text-left text-gray-700 font-medium py-3 border-b border-gray-50 last:border-0"
            >
              {l.label}
            </button>
          ))}
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 bg-blue-500 text-white font-bold px-5 py-3 rounded-xl mt-2"
          >
            <MessageCircle className="w-4 h-4" />
            Falar no WhatsApp
          </a>
        </motion.div>
      )}
    </header>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PHONE MOCKUP
// ─────────────────────────────────────────────────────────────────────────────

const PhoneMockup = () => (
  <div className="relative flex justify-center items-center py-8">
    {/* Glow */}
    <div className="absolute w-64 h-64 bg-blue-400/20 blur-3xl rounded-full" />

    {/* Phone frame */}
    <div className="relative w-56 md:w-64 bg-gray-900 rounded-[40px] shadow-2xl p-2.5 border-4 border-gray-800 z-10">
      {/* Side buttons */}
      <div className="absolute -right-[5px] top-20 w-[5px] h-8 bg-gray-700 rounded-r-sm" />
      <div className="absolute -right-[5px] top-32 w-[5px] h-8 bg-gray-700 rounded-r-sm" />
      <div className="absolute -left-[5px] top-24 w-[5px] h-12 bg-gray-700 rounded-l-sm" />

      {/* Screen */}
      <div className="bg-white rounded-[32px] overflow-hidden">
        {/* Notch */}
        <div className="relative flex justify-center pt-2.5 pb-1 bg-white">
          <div className="w-14 h-3.5 bg-gray-900 rounded-full" />
        </div>

        {/* Site header */}
        <div className="bg-blue-900 px-4 py-4">
          <p className="text-white font-black text-sm leading-tight">
            Auto Mecânica Confiança
          </p>
          <div className="flex items-center gap-0.5 mt-1">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star key={i} className="w-2.5 h-2.5 fill-yellow-400 text-yellow-400" />
            ))}
            <span className="text-blue-200 text-[10px] ml-1">4.9 · 127 avaliações</span>
          </div>
        </div>

        {/* Hero image */}
        <div className="bg-gradient-to-br from-orange-100 to-orange-200 h-24 flex items-center justify-center">
          <Wrench className="w-10 h-10 text-orange-400" />
        </div>

        {/* Content */}
        <div className="p-3 space-y-2.5">
          <div className="space-y-1.5">
            <div className="h-1.5 bg-gray-200 rounded-full w-full" />
            <div className="h-1.5 bg-gray-200 rounded-full w-4/5" />
            <div className="h-1.5 bg-gray-200 rounded-full w-3/5" />
          </div>

          <button className="w-full bg-green-500 text-white text-[10px] font-black py-2 rounded-lg">
            Pedir Orçamento no WhatsApp
          </button>

          <div className="grid grid-cols-2 gap-1.5">
            {["Alinhamento", "Balanceamento", "Freios", "Revisão"].map((s) => (
              <div key={s} className="bg-gray-50 border border-gray-100 rounded-md p-1.5 text-center">
                <span className="text-gray-700 text-[8px] font-medium">{s}</span>
              </div>
            ))}
          </div>

          <div className="bg-green-50 border border-green-100 rounded-lg h-10 flex items-center justify-center gap-1">
            <MapPin className="w-2.5 h-2.5 text-green-600" />
            <span className="text-green-700 text-[9px] font-medium">Ver no Google Maps</span>
          </div>

          {/* Bottom padding */}
          <div className="h-1" />
        </div>
      </div>
    </div>

    {/* Floating — 5 stars badge */}
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }}
      className="absolute -bottom-2 -left-4 md:-left-10 z-20 bg-white rounded-2xl shadow-xl border border-gray-100 px-3 py-2 flex items-center gap-2"
    >
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star key={i} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
        ))}
      </div>
      <span className="text-xs font-black text-gray-900">5 estrelas no Google</span>
    </motion.div>

    {/* Floating — WhatsApp badge */}
    <motion.div
      animate={{ y: [0, 8, 0] }}
      transition={{ repeat: Infinity, duration: 3.5, ease: "easeInOut", delay: 0.5 }}
      className="absolute top-10 -right-2 md:-right-8 z-20 bg-green-500 rounded-2xl shadow-lg px-3 py-2 flex items-center gap-1.5"
    >
      <MessageCircle className="w-3.5 h-3.5 text-white" />
      <span className="text-xs font-black text-white">WhatsApp direto</span>
    </motion.div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HERO
// ─────────────────────────────────────────────────────────────────────────────

const HeroSection = () => (
  <section id="hero" className="bg-white pt-20 md:pt-24 pb-16 md:pb-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left — text */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="text-center lg:text-left"
        >
          <motion.h1
            variants={fadeLeft}
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-tight tracking-tight mb-6"
          >
            Empresas bem avaliadas no Google merecem um{" "}
            <span className="text-blue-900">site profissional.</span>
          </motion.h1>

          <motion.p
            variants={fadeLeft}
            className="text-lg text-gray-600 leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0"
          >
            Você já faz um ótimo trabalho e tem a confiança dos seus clientes. Nós criamos um site
            moderno para a sua empresa ser encontrada, passar mais credibilidade e receber pedidos
            de orçamento direto no seu WhatsApp.
          </motion.p>

          <motion.div
            variants={fadeLeft}
            className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
          >
            <button
              data-testid="hero-examples-btn"
              onClick={() => scrollTo("exemplos")}
              className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-black px-8 py-4 rounded-xl transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 text-base"
            >
              Ver exemplo de site
              <ArrowRight className="w-5 h-5" />
            </button>

            <a
              data-testid="hero-whatsapp-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 border-2 border-gray-200 hover:border-gray-300 text-gray-700 hover:text-gray-900 font-bold px-8 py-4 rounded-xl transition-all duration-200 hover:bg-gray-50 text-base"
            >
              <MessageCircle className="w-5 h-5" />
              Falar no WhatsApp
            </a>
          </motion.div>

          {/* Social proof */}
          <motion.div
            variants={fadeLeft}
            className="mt-8 flex items-center gap-4 justify-center lg:justify-start"
          >
            <div className="flex -space-x-2">
              {[
                { color: "bg-orange-400", initial: "M" },
                { color: "bg-blue-500",   initial: "J" },
                { color: "bg-green-500",  initial: "R" },
                { color: "bg-purple-500", initial: "A" },
              ].map((a, i) => (
                <div
                  key={i}
                  className={`w-8 h-8 rounded-full ${a.color} border-2 border-white flex items-center justify-center text-white text-xs font-black`}
                >
                  {a.initial}
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-600">
              <span className="font-black text-gray-900">+50 empresas</span> já têm seu site
            </p>
          </motion.div>
        </motion.div>

        {/* Right — mockup */}
        <motion.div
          variants={fadeRight}
          initial="hidden"
          animate="visible"
          className="flex justify-center lg:justify-end"
        >
          <PhoneMockup />
        </motion.div>
      </div>
    </div>
  </section>
);

// ─────────────────────────────────────────────────────────────────────────────
// PROBLEM
// ─────────────────────────────────────────────────────────────────────────────

const PROBLEM_CARDS = [
  {
    icon: Shield,
    title: "Menos confiança",
    text: "Clientes pesquisam antes de comprar. Uma empresa sem site parece menos estruturada do que a concorrência.",
  },
  {
    icon: TrendingDown,
    title: "Menos clientes",
    text: "Quem procura um serviço no Google quer facilidade. Sem um site claro, o cliente desiste e procura outro profissional.",
  },
  {
    icon: Store,
    title: "Presença digital incompleta",
    text: "O Google Maps é ótimo, mas não conta a história da sua empresa nem explica todos os seus serviços em detalhes.",
  },
];

const ProblemSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section id="problema" className="bg-gray-50 py-20 md:py-28" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-14"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-4"
          >
            Só aparecer no mapa já não é suficiente.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Muitos negócios têm ótimas avaliações, mas perdem oportunidades todos os dias por não
            terem um endereço próprio na internet.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
        >
          {PROBLEM_CARDS.map((card, i) => {
            const Icon = card.icon;
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`problem-card-${i}`}
                className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-blue-100 transition-colors">
                  <Icon className="w-6 h-6 text-blue-900" />
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-3">{card.title}</h3>
                <p className="text-gray-600 leading-relaxed">{card.text}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SOLUTION
// ─────────────────────────────────────────────────────────────────────────────

const SOLUTION_FEATURES = [
  { icon: Star,           title: "Avaliações reais",           text: "Mostramos suas estrelas e os melhores comentários dos seus clientes para gerar confiança imediata." },
  { icon: Wrench,         title: "Seus serviços",              text: "Uma área dedicada para explicar exatamente o que você faz e atende." },
  { icon: MapPin,         title: "Localização",                text: "Mapa integrado para que achem sua oficina, loja ou escritório sem erro." },
  { icon: MessageCircle,  title: "Contato fácil",              text: "Um botão direto para o seu WhatsApp, transformando visitantes em contatos reais." },
];

const SolutionSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.12 });

  return (
    <section className="bg-white py-20 md:py-28" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left */}
          <motion.div
            variants={stagger}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
          >
            <motion.h2
              variants={fadeLeft}
              className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-6"
            >
              Seu negócio digitalizado de forma{" "}
              <span className="text-blue-900">simples e profissional.</span>
            </motion.h2>
            <motion.p variants={fadeLeft} className="text-lg text-gray-600 leading-relaxed mb-10">
              Nós criamos o seu site aproveitando o que você já tem de melhor: a sua reputação.
              Sem termos técnicos e sem dor de cabeça para você.
            </motion.p>

            <motion.div variants={stagger} className="space-y-6">
              {SOLUTION_FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div key={i} variants={fadeLeft} className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900 mb-1">{f.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{f.text}</p>
                    </div>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* Right — browser mockup */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            animate={inView ? "visible" : "hidden"}
            className="flex justify-center"
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-200">
              {/* Browser bar */}
              <div className="bg-gray-100 px-4 py-3 flex items-center gap-2 border-b border-gray-200">
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <div className="flex-1 bg-white rounded-md px-3 py-1.5 text-xs text-gray-500 border border-gray-200 font-mono">
                  mecanicaconfianca.com.br
                </div>
              </div>

              {/* Site header */}
              <div className="bg-blue-900 p-5">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-white font-black text-sm">Auto Mecânica Confiança</p>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
                <p className="text-blue-300 text-xs">Mais de 300 clientes satisfeitos em São Paulo</p>
              </div>

              {/* Features preview */}
              <div className="p-5 space-y-3">
                {SOLUTION_FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} className="flex items-center gap-3 py-1">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-black text-gray-900">{f.title}</p>
                        <div className="h-1.5 bg-gray-100 rounded-full w-32 mt-1" />
                      </div>
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    </div>
                  );
                })}

                <button className="w-full bg-green-500 text-white font-black py-3 rounded-xl text-sm mt-2">
                  Pedir Orçamento via WhatsApp
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// GALLERY
// ─────────────────────────────────────────────────────────────────────────────

const GALLERY_ITEMS = [
  { name: "Auto Mecânica Confiança", city: "São Paulo, SP",       icon: Wrench,   stars: 4.9, reviews: 312, bgClass: "gallery-card-0" },
  { name: "Elétrica Silva",          city: "Campinas, SP",         icon: Zap,      stars: 4.8, reviews: 187, bgClass: "gallery-card-1" },
  { name: "Serralheria Moderna",     city: "Belo Horizonte, MG",   icon: Building2,stars: 4.9, reviews: 94,  bgClass: "gallery-card-2" },
  { name: "Manutenção Express",      city: "Rio de Janeiro, RJ",   icon: Wrench,   stars: 5.0, reviews: 256, bgClass: "gallery-card-3" },
];

const GALLERY_GRADIENTS = [
  "linear-gradient(135deg, #f97316, #dc2626)",
  "linear-gradient(135deg, #eab308, #f97316)",
  "linear-gradient(135deg, #16a34a, #0d9488)",
  "linear-gradient(135deg, #2563eb, #4338ca)",
];

const GallerySection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });

  return (
    <section id="exemplos" className="bg-gray-50 py-20 md:py-28" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-14"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-4"
          >
            Veja como ficam os sites na prática.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Explore exemplos de negócios locais que já transmitem mais profissionalismo.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {GALLERY_ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={i}
                variants={fadeUp}
                data-testid={`gallery-card-${i}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 group"
              >
                {/* Placeholder visual */}
                <div
                  className="h-40 flex flex-col items-center justify-center p-4 relative overflow-hidden"
                  style={{ background: GALLERY_GRADIENTS[i] }}
                >
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-2 right-2 w-16 h-16 border-2 border-white rounded-full" />
                    <div className="absolute bottom-2 left-2 w-10 h-10 border border-white rounded-full" />
                  </div>
                  <Icon className="w-10 h-10 text-white/80 mb-3" />
                  <div className="space-y-1.5 w-full px-4">
                    <div className="h-1.5 bg-white/30 rounded-full w-3/4 mx-auto" />
                    <div className="h-1.5 bg-white/20 rounded-full w-1/2 mx-auto" />
                  </div>
                </div>

                {/* Card info */}
                <div className="p-4">
                  <div className="flex items-center gap-1 mb-2">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        className={`w-3 h-3 ${
                          s <= Math.floor(item.stars)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-200 fill-gray-200"
                        }`}
                      />
                    ))}
                    <span className="text-xs text-gray-500 ml-1">
                      {item.stars} ({item.reviews})
                    </span>
                  </div>
                  <h3 className="font-black text-gray-900 text-sm mb-1">{item.name}</h3>
                  <div className="flex items-center gap-1 text-gray-500 mb-4">
                    <MapPin className="w-3 h-3" />
                    <span className="text-xs">{item.city}</span>
                  </div>
                  <button
                    data-testid={`gallery-view-btn-${i}`}
                    className="w-full border-2 border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white text-xs font-black py-2 rounded-lg transition-all duration-200"
                  >
                    Ver site
                  </button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AUTHORITY
// ─────────────────────────────────────────────────────────────────────────────

const AuthoritySection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  const doubled = [...PARTNER_LOGOS, ...PARTNER_LOGOS];

  return (
    <section id="quem-somos" className="bg-white py-20 md:py-28" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-14"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-4xl font-black text-blue-900 tracking-tight mb-6"
          >
            Tecnologia de grandes empresas,{" "}
            <br className="hidden md:block" />
            acessível para o seu negócio.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            A Membrus foi fundada por engenheiros de software com mais de 10 anos de experiência.
            Nós construímos a tecnologia por trás de sites que processam milhões de acessos, e agora
            trazemos essa mesma qualidade para a sua empresa local.
          </motion.p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <p className="text-center text-xs font-black text-gray-400 uppercase tracking-widest mb-8">
            Empresas com as quais já trabalhamos
          </p>

          <div className="overflow-hidden">
            <div className="marquee-track flex items-center gap-10">
              {doubled.map((logo, i) => (
                <div key={i} className="flex-shrink-0 w-20 h-20 rounded-full overflow-hidden">
                  <img
                    src={logo.url}
                    alt={logo.name}
                    className="w-full h-full object-cover grayscale opacity-60 hover:opacity-100 hover:grayscale-0 transition-all duration-300"
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRICING
// ─────────────────────────────────────────────────────────────────────────────

const PRICING_FEATURES = [
  "Site profissional criado por especialistas",
  "Hospedagem inclusa sem custo extra",
  "Manutenção técnica completa",
  "Integração com o seu WhatsApp",
  "Avaliações do Google integradas",
  "Suporte humanizado e rápido",
];

const PricingSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });

  return (
    <section id="preco" className="bg-gray-50 py-20 md:py-28" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="text-center mb-14"
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-4xl font-black text-gray-900 tracking-tight mb-4"
          >
            Um investimento simples, sem surpresas.
          </motion.h2>
          <motion.p variants={fadeUp} className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Tenha uma presença digital forte, profissional e rápida, sem pagar os valores absurdos
            de agências tradicionais.
          </motion.p>
        </motion.div>

        <motion.div
          variants={scaleUp}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          className="max-w-lg mx-auto"
        >
          <div
            data-testid="pricing-card"
            className="bg-white rounded-3xl border border-gray-200 shadow-2xl overflow-hidden"
          >
            {/* Card top */}
            <div className="bg-blue-900 p-8 text-center">
              <p className="text-blue-300 text-sm font-black uppercase tracking-widest mb-4">
                Site Profissional
              </p>
              <p className="text-blue-200 text-sm mb-2">Sites profissionais para empresas locais</p>
              <div className="flex items-end justify-center gap-1 mt-4">
                <span className="text-blue-200 text-lg font-black self-start mt-3">a partir de</span>
              </div>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span className="text-white text-3xl font-black">R$</span>
                <span className="text-white text-8xl font-black leading-none tracking-tighter">197</span>
                <span className="text-blue-200 text-xl self-end mb-3 font-bold">/mês</span>
              </div>
            </div>

            {/* Card body */}
            <div className="p-8">
              <div className="space-y-4 mb-6">
                {PRICING_FEATURES.map((feat, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700">{feat}</span>
                  </div>
                ))}
              </div>

              <p className="text-sm text-gray-500 text-center mb-6">
                (Inclui a criação, hospedagem e manutenção técnica. Nós cuidamos de tudo).
              </p>

              <a
                data-testid="pricing-cta-btn"
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-blue-500 hover:bg-blue-600 text-white font-black py-4 rounded-xl transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 text-base"
              >
                <MessageCircle className="w-5 h-5" />
                Quero meu site agora
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FINAL CTA
// ─────────────────────────────────────────────────────────────────────────────

const CTASection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.25 });

  return (
    <section className="bg-[#1E3A8A] py-20 md:py-28" ref={ref}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2
            variants={fadeUp}
            className="text-3xl md:text-5xl font-black text-white tracking-tight mb-6 leading-tight"
          >
            Pronto para ter o site da sua empresa?
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="text-xl text-blue-200 mb-10 leading-relaxed max-w-2xl mx-auto"
          >
            Não deixe seus concorrentes levarem os clientes que procuram pelo seu serviço.
            Dê o próximo passo hoje mesmo.
          </motion.p>
          <motion.div variants={fadeUp}>
            <a
              data-testid="final-cta-btn"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 bg-green-500 hover:bg-green-600 text-white font-black text-lg px-10 py-5 rounded-2xl transition-all duration-200 hover:shadow-2xl hover:-translate-y-1"
            >
              <MessageCircle className="w-6 h-6" />
              Quero um site para minha empresa
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// FOOTER
// ─────────────────────────────────────────────────────────────────────────────

const Footer = () => (
  <footer className="bg-gray-50 border-t border-gray-200 py-10">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="text-center md:text-left">
          <MembrusLogo width={100} height={28} />
          <p className="text-sm text-gray-500 mt-1">
            © 2026 Membrus. Criando o futuro dos pequenos negócios locais.
          </p>
        </div>
        <div className="flex items-center gap-6">
          <button
            data-testid="footer-privacy-link"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Política de Privacidade
          </button>
          <span className="text-gray-300">|</span>
          <button
            data-testid="footer-terms-link"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            Termos de Uso
          </button>
        </div>
      </div>
    </div>
  </footer>
);

// ─────────────────────────────────────────────────────────────────────────────
// LANDING PAGE (ROOT)
// ─────────────────────────────────────────────────────────────────────────────

const LandingPage = () => (
  <div style={{ fontFamily: "'Roboto', sans-serif" }}>
    <Header />
    <main>
      <HeroSection />
      <ProblemSection />
      <SolutionSection />
      <GallerySection />
      <AuthoritySection />
      <PricingSection />
      <CTASection />
    </main>
    <Footer />
  </div>
);

export default LandingPage;
